import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Receipt, HardHat, ChevronRight, BookOpen } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { api } from '../lib/api';
import { PageHeader, Loading, EmptyState } from '../components/Shared';
import { ReviewBadge, SourceNote, SampleDataBanner } from '../components/DataBadge';
import { DatasetChart } from '../components/ChartBlocks';
import { useCampaignContent, CityDataTaxIntro, ReportTrackBlock, TaxPromiseSection } from '../components/CampaignBlocks';

const CATEGORY_TABS = [
  { value: 'all', label: 'All' },
  { value: 'taxes', label: 'Taxes' },
  { value: 'budget', label: 'Budget' },
  { value: 'population', label: 'Population' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'roads', label: 'Road Projects' },
];

export default function CityData() {
  const [category, setCategory] = useState('all');
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/datasets').then((r) => setDatasets(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = category === 'all' ? datasets : datasets.filter((d) => d.category === category);
  const campaign = useCampaignContent();

  return (
    <div>
      <PageHeader pageKey="city-data" kicker="CITY DATA DASHBOARD" title="Understand Your City" subtitle="Taxes, budgets, population, infrastructure spending, and road projects, in plain language, with sources." />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* City Data Should Help Lower Taxes */}
        <div className="mb-6"><CityDataTaxIntro /></div>

        <SampleDataBanner text='Every chart below is labeled with its verification status. Charts marked "Sample data" have NOT been verified against official documents. Always check the source link before relying on a figure.' testId="citydata-sample-banner" />

        {/* Deep-dive links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 mb-8" data-testid="citydata-deep-dives">
          <Link to="/tax-tracker" data-testid="citydata-taxtracker-link">
            <Card className="card-lift h-full border-[#C9A84C]">
              <CardContent className="p-5 flex items-center gap-4">
                <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#1a2744] shrink-0"><Receipt className="h-5 w-5 text-[#C9A84C]" /></span>
                <div className="flex-1">
                  <p className="font-serif font-bold text-[#1a2744]">Tax Tracker</p>
                  <p className="text-xs text-slate-500 mt-0.5">Levy history, where the dollars go, and questions to ask</p>
                </div>
                <ChevronRight className="h-4 w-4 text-[#C9A84C]" />
              </CardContent>
            </Card>
          </Link>
          <Link to="/infrastructure" data-testid="citydata-infra-link">
            <Card className="card-lift h-full border-[#C9A84C]">
              <CardContent className="p-5 flex items-center gap-4">
                <span className="inline-flex items-center justify-center h-11 w-11 rounded-xl bg-[#1a2744] shrink-0"><HardHat className="h-5 w-5 text-[#C9A84C]" /></span>
                <div className="flex-1">
                  <p className="font-serif font-bold text-[#1a2744]">Infrastructure Tracker</p>
                  <p className="text-xs text-slate-500 mt-0.5">Roads, bridges & capital projects for Ward 8</p>
                </div>
                <ChevronRight className="h-4 w-4 text-[#C9A84C]" />
              </CardContent>
            </Card>
          </Link>
        </div>

        <Tabs value={category} onValueChange={setCategory}>
          <TabsList className="bg-white border border-slate-200 h-auto p-1 flex-wrap justify-start w-full sm:w-auto" data-testid="citydata-filters">
            {CATEGORY_TABS.map((t) => (
              <TabsTrigger key={t.value} value={t.value} className="h-10 px-4 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid={`citydata-tab-${t.value}`}>{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {loading ? <Loading /> : filtered.length === 0 ? (
          <EmptyState title="No datasets in this category yet" subtitle="The campaign team adds and updates datasets from the admin dashboard." />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">
            {filtered.map((ds) => (
              <Card key={ds.id} className="card-lift" data-testid="citydata-chart">
                <CardContent className="p-5 sm:p-6">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-[#C9A84C]">{ds.category}</p>
                    <ReviewBadge status={ds.review_status} />
                  </div>
                  <h3 className="font-serif font-bold text-[#1a2744] text-lg leading-snug mt-1">{ds.title}</h3>
                  <div className="h-64 mt-4">
                    <DatasetChart ds={ds} />
                  </div>
                  {ds.description && <p className="text-sm text-slate-600 mt-4 leading-relaxed">{ds.description}</p>}
                  {ds.more_info_url && (
                    <div className="mt-4">
                      <Button asChild size="sm" className="h-10 px-4 font-bold bg-[#1a2744] hover:bg-[#243456] text-white btn-press" data-testid="citydata-more-info-btn">
                        <Link to={ds.more_info_url} aria-label={`${ds.more_info_label || 'More Info'}: ${ds.more_info_article_title || ds.title}`}>
                          <BookOpen className="h-4 w-4 mr-1.5" /> {ds.more_info_label || 'More Info'}
                        </Link>
                      </Button>
                    </div>
                  )}
                  <SourceNote sourceName={ds.source_name} sourceUrl={ds.source_url} lastUpdated={ds.last_updated} testId="citydata-source-note" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs text-amber-900 leading-relaxed" data-testid="citydata-disclaimer">Charts are maintained by the campaign team from public sources. Always verify figures against official City of Kawartha Lakes documents. Source links are provided on each chart, and charts without a confirmed source are marked “Data source needed.”</p>
        </div>

        {campaign?.tax_promise?.sections?.citydata_tax_promise !== false && (
          <div className="mt-6"><TaxPromiseSection promise={campaign?.tax_promise} compact /></div>
        )}
      </div>

      {/* Report It. Track It. Cut the Waste. Lower the Taxes. */}
      <ReportTrackBlock promise={campaign?.tax_promise} />
    </div>
  );
}
